import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Shop } from 'components/pages/Shop';
import {
  fetchProductListingPage,
  PRODUCT_LISTING_PAGE_KEY,
  SHOP_ALL,
} from 'hooks/pages/useProductListingPage';
import {
  fetchProductListingPages,
  PRODUCT_LISTING_PAGES_KEY,
} from 'hooks/pages/useProductListingPages';
import { env } from 'lib/constants/env';
import type { SiteCategory } from 'types/common/SiteCategory';
import { blocksBuildTimeResolver, prefetchCommonData } from 'utils/buildTime';

import { fetchWithSentry } from './fetchWithSentry';

interface ShopIndexPageOptions {
  category: SiteCategory;
}

export function shopIndexPage({ category }: ShopIndexPageOptions) {
  async function getStaticProps() {
    const queryClient = new QueryClient();

    await queryClient.prefetchQuery({
      queryKey: [PRODUCT_LISTING_PAGES_KEY, category],
      queryFn: fetchProductListingPages,
    });

    const page = await fetchWithSentry({
      queryClient,
      queryKey: [PRODUCT_LISTING_PAGE_KEY, SHOP_ALL, category],
      queryFn: fetchProductListingPage,
      tags: { template: 'shopIndexPage', api: 'sanity' },
      isRequired: true,
    });

    await prefetchCommonData(queryClient);
    await blocksBuildTimeResolver(queryClient, page.blocks);

    return {
      revalidate: env.ISR_TTL_SECONDS,
      props: {
        dehydratedState: dehydrate(queryClient),
      },
    };
  }

  const ShopIndexPageComponent = () => <Shop slug={SHOP_ALL} />;

  return {
    getStaticProps,
    ShopIndexPageComponent,
  };
}
